import { useState, useCallback, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { addItem, updateItem, getItemGroupList } from 'helpers/backend_helper';
import { Common } from 'helpers/common';

export const useItemModal = (onSave: (result: any) => void, initialData?: any) => {
    const [show, setShow] = useState<boolean>(false);
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [itemData, setItemData] = useState<any>(initialData);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [itemGroups, setItemGroups] = useState<any[]>([]);
    const commonHelper = new Common();

    useEffect(() => {
        if (initialData) {
            setItemData(initialData);
            setIsEdit(!!initialData.id);
        }
    }, [initialData]);

    useEffect(() => {
        fetchItemGroups();
    }, []);

    const fetchItemGroups = useCallback(async () => {
        try {
            const response = await getItemGroupList();
            setItemGroups(response.data || response);
        } catch (error: any) {
            console.error("Error fetching item groups:", error);
        }
    }, []);

    const toggle = useCallback(() => {
        setShow(!show);
        if (show) {
            setItemData(null);
        }
    }, [show]);

    const handleEditDataClick = (ele: any) => {
        setItemData({ ...ele });
        setIsEdit(true);
        setShow(true);
    };

    const handleAddItem = () => {
        setItemData({});
        toggle();
    };

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            item_code: (itemData && itemData.item_code) || "",
            item_name: (itemData && itemData.item_name) || "",
            item_group_id: (itemData && itemData.item_group_id) || "",
        },
        validationSchema: Yup.object({
            item_code: Yup.string().required("Item code is required"),
            item_name: Yup.string().required("Item name is required"),
            item_group_id: Yup.string().required("Item group is required"),
        }),
        onSubmit: async (values) => {
            try {
                setIsSaving(true);
                let result;
                const formattedValues = {
                    ...values,
                    item_group_id: parseInt(values.item_group_id),
                };

                if (isEdit && itemData?.id) {
                    result = await updateItem({
                        id: itemData.id,
                        ...formattedValues,
                    });
                    commonHelper.showSuccessMessage('Item updated successfully!');
                } else {
                    result = await addItem(formattedValues);
                    commonHelper.showSuccessMessage('Item added successfully!');
                }
                
                onSave(result);
                validation.resetForm();
            } catch (error: any) {
                commonHelper.showErrorMessage('Operation failed!');
            } finally {
                setIsSaving(false);
            }
        },
    });

    return {
        show,
        isEdit,
        itemData,
        toggle,
        handleEditDataClick,
        handleAddItem,
        validation,
        isSaving,
        itemGroups,
    };
};